import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap, useScrollLock } from '@/shared/hooks/useFocusTrap';
import { X } from 'lucide-react';
import { Icon } from '@/shared/components/Icon';
import { cn } from '@/shared/helpers/cn';
import styles from './BottomSheet.module.scss';

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

/**
 * Folha inferior — o padrao de sobreposicao em tela pequena.
 *
 * Sobe a no maximo 90vh com conteudo rolavel e a acao principal no rodape, ao
 * alcance do polegar. A alca no topo e o sinal visual de que a folha e
 * dispensavel; o botao Fechar existe porque o gesto sozinho nao e alcancavel por
 * teclado.
 */
export function BottomSheet({ open, onClose, title, children, footer, className }: BottomSheetProps) {
  const titleId = useId();
  const sheetRef = useRef<HTMLDivElement>(null);

  useFocusTrap(sheetRef, open, onClose);
  useScrollLock(open);

  if (!open) return null;

  return createPortal(
    <div className={styles.backdrop} onMouseDown={onClose}>
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(styles.sheet, className)}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.handle} aria-hidden="true" />

        <header className={styles.header}>
          <h2 className={styles.title} id={titleId}>
            {title}
          </h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Fechar">
            <Icon as={X} size={20} />
          </button>
        </header>

        <div className={styles.body}>{children}</div>

        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
